import * as React from 'react';
import { dashToCamel, isSSR, AppHydration } from 'oasis-os-utils';
import { getCachedModule, loadFeatureApp } from './loader';
import useDynamicFeature from './useDynamicFeature';
import ErrorBoundary from './ErrorBoundary';
import { constructUrl, autoFixFeatureAppUrl, getFeatureAppUrlParts, } from './url';
const Feature = ({ src, name = '', version, sharedModule = './App', loader = null, fallback, onLoad, onFail, hydrate = false, ...rest }) => {
    const featureAppRemoteUrl = autoFixFeatureAppUrl(src || constructUrl({ appName: name, version: version }));
    let library = name;
    if (!name) {
        // If library is not supplied then figure it out from the remote url source
        const parts = getFeatureAppUrlParts(featureAppRemoteUrl);
        if (parts && parts.name) {
            library = dashToCamel(parts.name);
        }
        else {
            if (typeof onFail === 'function') {
                onFail();
            }
            // Looks like we couldn't resolve the name
            throw new Error('Cannot resolve library name');
        }
    }
    const status = useDynamicFeature(featureAppRemoteUrl);
    if (status === 'error') {
        if (fallback) {
            if (typeof onFail === 'function') {
                onFail();
            }
            return React.createElement(React.Fragment, null, fallback);
        }
        return process.env.NODE_ENV === 'development' ? (React.createElement("h2", null,
            "Failed to load feature ",
            React.createElement("i", null, library),
            " from ",
            featureAppRemoteUrl)) : null;
    }
    if (!isSSR()) {
        // Try to retrieve feature app from the cache
        const CachedFeatureApp = getCachedModule(library, sharedModule);
        if (CachedFeatureApp) {
            if (typeof onLoad === 'function') {
                // Allow the current execution loop to expire before calling onLoad
                setTimeout(onLoad, 0);
            }
            return React.createElement(CachedFeatureApp, { ...rest });
        }
    }
    if (status === 'ready') {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const isHydrate = AppHydration.getHydration() || hydrate;
        const loadingPromise = loadFeatureApp(library, sharedModule, isHydrate)();
        // Cache failed, but the module loaded asynchronously. Time to async-load and show
        const Component = React.lazy(() => loadingPromise);
        loadingPromise.then(() => {
            if (process.env.NODE_ENV === 'development') {
                console.log(`[OASIS] Feature app %c${library} %cloaded `, 'color: #c59485', 'color: inherit;');
            }
            if (typeof onLoad === 'function') {
                // Allow the current execution loop to expire before calling onLoad
                setTimeout(onLoad, 0);
            }
        });
        return (React.createElement(ErrorBoundary, null,
            React.createElement(React.Suspense, { fallback: loader },
                React.createElement(Component, { ...rest, app: { name: library, url: src, version, sharedModule } }))));
    }
    // Idle....
    return process.env.NODE_ENV === 'development' ? (React.createElement("h2", null,
        "Loading feature ",
        React.createElement("i", null, library),
        " from ",
        featureAppRemoteUrl)) : null;
};
export async function preloadFeatureApps() {
    const apps = window.__PRELOAD_FEATURES__ || {};
    const thenables = Object.values(apps).map(([name, sharedModule]) => loadFeatureApp(name, sharedModule, true)());
    return Promise.all(thenables);
}
export async function onFeaturesReady(cb) {
    await preloadFeatureApps();
    cb();
}
export default Feature;
